import {
  getStatus,
  getIconType,
  getImportance,
  getSolvability,
} from '../../constants/seoMappings';

/**
 * Generates message for Twitter Card section
 * @param {Array} properties - The properties array from API
 * @returns {string|null} - Message for empty state or null if data exists
 */
const generateMessage = (properties) => {
  if (!properties || properties?.length === 0) {
    return 'No Twitter Card was found.';
  }
  return null;
};

/**
 * Builds properties table from API properties array
 * @param {Array} properties - Array of { name, content } objects
 * @returns {Array} - Array of table row objects
 */
const buildPropertiesTable = (properties) => {
  if (!properties || properties?.length === 0) {
    return [];
  }

  return properties.map((item) => ({
    tag: item?.name || '',
    value: item?.content || '',
  }));
};

/**
 * Builds errors table from API errors array
 * @param {Array} errors - Array of { meta: { property }, type } objects
 * @returns {Array} - Array of table row objects for errors
 */
const buildErrorsTable = (errors) => {
  if (!errors || errors?.length === 0) {
    return [];
  }

  return errors.map((item) => ({
    property: item?.meta?.property || '',
    type: item?.type || '',
  }));
};

/**
 * Transforms the twitter_card criteria from WooRank API response
 * @param {Object} twitterCardCriteria - The twitter_card criteria object from the API
 * @returns {Object} - Transformed Twitter Card data for React components
 */
export const transformTwitterCard = (twitterCardCriteria) => {
  // Handle missing criteria - use 'neutral' status (maps to 'info' icon)
  if (!twitterCardCriteria) {
    return {
      status: getStatus('neutral'),
      iconType: getIconType('neutral'),
      importance: { level: 0, text: '' },
      solvability: { level: 0, text: '' },
      isEmpty: true,
      message: 'No Twitter Card was found.',
      data: null,
    };
  }

  const data = twitterCardCriteria?.data || {};
  const wooRankStatus = twitterCardCriteria?.status || 'neutral';
  const importance = twitterCardCriteria?.importance || 0;
  const solvability = twitterCardCriteria?.solvability || 0;

  const properties = data?.properties || [];
  const errors = data?.errors || [];

  const propertiesTable = buildPropertiesTable(properties);
  const errorsTable = buildErrorsTable(errors);

  const isEmpty = propertiesTable.length === 0;

  return {
    status: getStatus(wooRankStatus),
    iconType: getIconType(wooRankStatus),
    importance: getImportance(importance),
    solvability: getSolvability(solvability),
    isEmpty,
    hasErrors: errorsTable.length > 0,
    message: generateMessage(properties),
    data: isEmpty ? null : {
      propertiesTable,
      errorsTable,
    },
  };
};

export default transformTwitterCard;

